/**
 * COMPOSANT: TimelineEvent
 * Affichage d'un événement individuel dans la timeline (design Figma)
 */

import {
  Phone,
  PhoneOutgoing,
  PhoneIncoming,
  PhoneOff,
  Mail,
  MailWarning,
  StickyNote,
  RefreshCw,
  UserCheck,
  Plus,
  FileEdit,
  CheckCircle,
  Calendar,
  Clock,
  Zap,
  CreditCard,
  Users,
  BadgeCheck,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TimelineEvent as TimelineEventType } from '@/types/timeline';
import { TIMELINE_EVENT_COLORS, TIMELINE_EVENT_TYPE } from '@/types/timeline';
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from '@/types/payment';
import { formatTimeAgo } from '@/lib/date-utils';

function paymentMethodLabel(method: unknown): string | null {
  if (typeof method !== 'string') return null;
  return PAYMENT_METHOD_LABELS[method as PaymentMethod] ?? method;
}

interface TimelineEventProps {
  event: TimelineEventType;
  isLast?: boolean;
}

export function TimelineEvent({ event }: TimelineEventProps) {
  // Couleurs selon le type d'événement et métadonnées (pour les appels)
  const getColors = () => {
    if (event.event_type === TIMELINE_EVENT_TYPE.CALL_LOGGED && event.metadata) {
      const direction = event.metadata.direction;
      const outcome = event.metadata.outcome;

      // Appel non abouti (no_answer ou voicemail)
      if (outcome === 'no_answer' || outcome === 'voicemail') {
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          border: 'border-gray-500',
        };
      }

      // Appel entrant répondu
      if (direction === 'incoming') {
        return {
          bg: 'bg-indigo-100',
          text: 'text-indigo-700',
          border: 'border-indigo-500',
        };
      }

      // Appel sortant répondu (par défaut)
      return {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        border: 'border-blue-500',
      };
    }

    return (
      TIMELINE_EVENT_COLORS[event.event_type] ||
      TIMELINE_EVENT_COLORS[TIMELINE_EVENT_TYPE.ENTITY_CREATED]
    );
  };

  const colors = getColors();

  // Icône selon le type d'événement (size-4 pour 16px dans un conteneur 32px)
  const getIcon = () => {
    const iconClass = 'size-4';

    switch (event.event_type) {
      case TIMELINE_EVENT_TYPE.CALL_LOGGED:
        // Icônes différentes selon direction et outcome
        if (event.metadata) {
          const direction = event.metadata.direction;
          const outcome = event.metadata.outcome;

          // Appel non abouti
          if (outcome === 'no_answer' || outcome === 'voicemail') {
            return <PhoneOff className={iconClass} />;
          }

          // Appel entrant
          if (direction === 'incoming') {
            return <PhoneIncoming className={iconClass} />;
          }

          // Appel sortant
          return <PhoneOutgoing className={iconClass} />;
        }
        return <Phone className={iconClass} />;

      case TIMELINE_EVENT_TYPE.EMAIL_SENT:
      case TIMELINE_EVENT_TYPE.EMAIL_RECEIVED:
        return <Mail className={iconClass} />;
      case TIMELINE_EVENT_TYPE.EMAIL_BOUNCED:
        return <MailWarning className={iconClass} />;
      case TIMELINE_EVENT_TYPE.NOTE_CREATED:
        return <StickyNote className={iconClass} />;
      case TIMELINE_EVENT_TYPE.STATUS_CHANGED:
        return <RefreshCw className={iconClass} />;
      case TIMELINE_EVENT_TYPE.ASSIGNED_USER_CHANGED:
        return <UserCheck className={iconClass} />;
      case TIMELINE_EVENT_TYPE.FIELD_UPDATED: // @deprecated
        return <FileEdit className={iconClass} />;
      case TIMELINE_EVENT_TYPE.ENTITY_CREATED:
        return <Plus className={iconClass} />;
      case TIMELINE_EVENT_TYPE.TASK_CREATED:
        return <Calendar className={iconClass} />;
      case TIMELINE_EVENT_TYPE.TASK_COMPLETED:
        return <CheckCircle className={iconClass} />;
      case TIMELINE_EVENT_TYPE.TASK_RESCHEDULED:
        return <Clock className={iconClass} />;
      case TIMELINE_EVENT_TYPE.SEQUENCE_SENT:
        return <Zap className={iconClass} />;
      case TIMELINE_EVENT_TYPE.DATA_UPDATED:
        return <FileEdit className={iconClass} />;
      case TIMELINE_EVENT_TYPE.PAYMENT_PAID:
      case TIMELINE_EVENT_TYPE.PAYMENT_METHOD_SELECTED:
      case TIMELINE_EVENT_TYPE.PAYMENT_METHOD_CHANGED:
      case TIMELINE_EVENT_TYPE.PAYMENT_INITIATED:
        return <CreditCard className={iconClass} />;
      case TIMELINE_EVENT_TYPE.PAYMENT_PAYER_IDENTIFIED:
      case TIMELINE_EVENT_TYPE.PAYMENT_PAYER_CHANGED:
        return <Users className={iconClass} />;
      case TIMELINE_EVENT_TYPE.BPI_MANUALLY_VALIDATED:
        return <BadgeCheck className={iconClass} />;
      case TIMELINE_EVENT_TYPE.PAYMENT_REFUND_RECORDED:
        return <Undo2 className={iconClass} />;
      default:
        return <FileEdit className={iconClass} />;
    }
  };

  const iconBg = cn('rounded-lg flex items-center justify-center shrink-0 size-8', colors.bg);

  // Formater les métadonnées pour l'affichage
  const renderMetadata = () => {
    if (!event.metadata) return null;

    switch (event.event_type) {
      case TIMELINE_EVENT_TYPE.CALL_LOGGED:
        return (
          <>
            {/* Durée de l'appel si disponible et > 0 */}
            {event.metadata.duration !== undefined && event.metadata.duration > 0 && (
              <p className="text-sm text-zinc-600 mt-1">
                Conversation de {event.metadata.duration} min.
              </p>
            )}
            {/* Note d'appel avec encadré jaune */}
            {event.metadata.notes && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm">
                  <span className="font-bold text-amber-900">Note : </span>
                  <span className="text-amber-900">{event.metadata.notes}</span>
                </p>
              </div>
            )}
          </>
        );

      case TIMELINE_EVENT_TYPE.EMAIL_SENT:
      case TIMELINE_EVENT_TYPE.EMAIL_RECEIVED:
        return (
          <>
            {/* Afficher uniquement l'objet de l'email */}
            {event.metadata.subject && (
              <div className="mt-2 bg-zinc-50/50 border border-zinc-200 rounded-lg p-3">
                <p className="text-sm text-zinc-700">
                  <span className="font-medium">Objet : </span>
                  {event.metadata.subject}
                </p>
              </div>
            )}
          </>
        );

      case TIMELINE_EVENT_TYPE.SEQUENCE_SENT:
        return <p className="text-sm text-zinc-900 mt-1">Thème : {event.metadata.theme}</p>;

      case TIMELINE_EVENT_TYPE.ASSIGNED_USER_CHANGED:
        // La description suffit (ex: "Jean Dupont → Marie Martin")
        return null;

      case TIMELINE_EVENT_TYPE.PAYMENT_PAID:
        return event.metadata.comment ? (
          <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-900">{event.metadata.comment}</p>
          </div>
        ) : null;

      // 00295 : commentaire du staff (référence du virement de remboursement).
      case TIMELINE_EVENT_TYPE.PAYMENT_REFUND_RECORDED:
        return event.metadata.comment ? (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">{event.metadata.comment}</p>
          </div>
        ) : null;

      // T1 : attestation « BPI réglé » posée à la main. Le montant est LE fait
      // à tracer (il est défalqué du solde BED) — on l'affiche en clair, sans
      // laisser croire à un encaissement.
      case TIMELINE_EVENT_TYPE.BPI_MANUALLY_VALIDATED: {
        const cents = event.metadata.amount_cents;
        if (typeof cents !== 'number') return null;
        return (
          <p className="text-sm text-zinc-600 mt-1">
            Montant déduit du solde BED :{' '}
            <span className="font-medium text-zinc-900">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
                cents / 100
              )}
            </span>
          </p>
        );
      }

      case TIMELINE_EVENT_TYPE.PAYMENT_INITIATED:
      case TIMELINE_EVENT_TYPE.PAYMENT_METHOD_SELECTED: {
        // Affiche la methode choisie (CB / Virement / Cheque) en clair, plutot
        // que de devoir la deviner dans le titre.
        const methodLabel = paymentMethodLabel(event.metadata.method);
        if (!methodLabel) return null;
        return (
          <p className="text-sm text-zinc-600 mt-1">
            Méthode : <span className="font-medium text-zinc-900">{methodLabel}</span>
          </p>
        );
      }

      case TIMELINE_EVENT_TYPE.PAYMENT_METHOD_CHANGED: {
        // Cas du changement : metadata.from + metadata.to (cf. transfer-selected /
        // cheque-selected cote 4LT). Fallback sur method si from/to absents.
        const fromLabel = paymentMethodLabel(event.metadata.from);
        const toLabel =
          paymentMethodLabel(event.metadata.to) ?? paymentMethodLabel(event.metadata.method);
        if (!fromLabel && !toLabel) return null;
        return (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">
              <span className="font-bold">Changement :</span> <span>{fromLabel ?? '—'}</span> →{' '}
              <span className="font-medium">{toLabel ?? '—'}</span>
            </p>
          </div>
        );
      }

      case TIMELINE_EVENT_TYPE.PAYMENT_PAYER_CHANGED:
        return event.metadata.previous_payer_contact_id && event.metadata.payer_contact_id ? (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">
              <span className="font-bold">Changement :</span>{' '}
              <span className="font-mono text-xs">{event.metadata.previous_payer_contact_id}</span>{' '}
              → <span className="font-mono text-xs">{event.metadata.payer_contact_id}</span>
            </p>
          </div>
        ) : null;

      case TIMELINE_EVENT_TYPE.FIELD_UPDATED: // @deprecated
      case TIMELINE_EVENT_TYPE.STATUS_CHANGED:
      case TIMELINE_EVENT_TYPE.DATA_UPDATED:
      default:
        return null;
    }
  };

  return (
    <div className="flex gap-3 py-3 border-b border-zinc-100 last:border-0">
      {/* Icône 32px selon Figma */}
      <div className={iconBg}>
        <div className={colors.text}>{getIcon()}</div>
      </div>

      {/* Contenu */}
      <div className="flex-1">
        {/* Titre */}
        <p className="text-base text-zinc-900 mb-1">{event.title}</p>

        {/* Date et auteur */}
        <p className="text-sm text-zinc-500">
          {formatTimeAgo(event.created_at)}
          {event.created_by_user &&
            ` • par ${event.created_by_user.full_name || event.created_by_user.email}`}
        </p>

        {/* Description */}
        {event.description && <p className="text-base text-zinc-500 mt-1">{event.description}</p>}

        {/* Métadonnées formatées */}
        {renderMetadata()}
      </div>
    </div>
  );
}
